import React from "react";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Eyebrow, ArrowLink, Button } from "./ui-bits";
import { ChevronDown, ArrowRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

// ---- Page hero used across inner pages ----
export const PageHero = ({ eyebrow, title, sub, meta }) => (
  <section className="px-5 sm:px-8 lg:px-14 pt-28 sm:pt-36 lg:pt-44 pb-12 sm:pb-24">
    <div className="max-w-[1400px] mx-auto">
      <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.05}>
        <h1 data-testid="page-title" className="display-hero mt-6 sm:mt-7 text-4xl sm:text-6xl lg:text-[5.5rem] leading-[1.05] text-ws-dark max-w-5xl text-balance">{title}</h1>
      </Reveal>
      {sub && <Reveal delay={0.12}><p className="mt-8 text-lg sm:text-xl text-ws-text2 max-w-2xl leading-relaxed">{sub}</p></Reveal>}
      {meta && (
        <Reveal delay={0.18}>
          <div className="mt-10 flex flex-wrap gap-x-4 gap-y-2">
            {meta.map((m) => <span key={m} className="label text-ws-text2 border border-ws-border rounded-full px-4 py-2">{m}</span>)}
          </div>
        </Reveal>
      )}
    </div>
  </section>
);

// ---- Product card ----
export const ProductCard = ({ item, index }) => (
  <Reveal delay={(index % 3) * 0.08} className="group">
    <div className="bg-ws-card border border-ws-border rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-500 hover:border-ws-accent hover:-translate-y-1">
      <div className="relative overflow-hidden aspect-[4/3] bg-ws-bg2">
        <img src={item.img} alt={`${item.title} — conceptual unbranded product mockup`} loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute bottom-3 left-3 label text-ws-dark bg-ws-bg/90 backdrop-blur rounded-full px-3 py-1.5 text-[0.62rem]">{item.tag}</span>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-2xl text-ws-dark leading-tight">{item.title}</h3>
        <p className="text-ws-text2 text-sm mt-3 leading-relaxed flex-1">{item.desc}</p>
      </div>
    </div>
  </Reveal>
);

// ---- Capability card ----
export const CapabilityCard = ({ item, index, dark = false }) => {
  const Icon = Icons[item.icon] || Icons.Box;
  return (
    <Reveal delay={(index % 3) * 0.07}>
      <div className={`group h-full p-6 sm:p-8 rounded-2xl border transition-[transform,border-color] duration-500 hover:-translate-y-1 ${dark ? "bg-ws-dark2 border-ws-bg/10 hover:border-ws-accent" : "bg-ws-card border-ws-border hover:border-ws-accent"}`}>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-6 transition-colors ${dark ? "bg-ws-bg/10 group-hover:bg-ws-accent" : "bg-ws-lightAccent group-hover:bg-ws-accent"}`}>
          <Icon className={`w-5 h-5 ${dark ? "text-ws-bg" : "text-ws-accentHover group-hover:text-white"} transition-colors`} strokeWidth={1.6} />
        </div>
        <h3 className={`font-display text-2xl leading-tight ${dark ? "text-ws-bg" : "text-ws-dark"}`}>{item.title}</h3>
        <p className={`text-sm mt-3 leading-relaxed ${dark ? "text-ws-bg/60" : "text-ws-text2"}`}>{item.desc}</p>
      </div>
    </Reveal>
  );
};

// ---- Numbered process step (vertical) ----
export const ProcessStep = ({ step, index, last, dark = false }) => (
  <Reveal delay={index * 0.06}>
    <div className="relative flex gap-4 sm:gap-10 pb-10 sm:pb-12 group">
      <div className="flex flex-col items-center shrink-0">
        <span className={`font-display text-lg w-12 h-12 sm:w-14 sm:h-14 rounded-full border flex items-center justify-center transition-colors ${dark ? "border-ws-bg/25 text-ws-bg group-hover:bg-ws-accent group-hover:border-ws-accent" : "border-ws-border text-ws-dark group-hover:bg-ws-dark group-hover:text-ws-bg group-hover:border-ws-dark"}`}>{step.n}</span>
        {!last && <span className={`w-px flex-1 mt-3 ${dark ? "bg-ws-bg/15" : "bg-ws-border"}`} />}
      </div>
      <div className="pt-2 pb-2">
        <h3 className={`font-display text-xl sm:text-3xl leading-tight ${dark ? "text-ws-bg" : "text-ws-dark"}`}>{step.title}</h3>
        <p className={`mt-3 max-w-xl leading-relaxed ${dark ? "text-ws-bg/60" : "text-ws-text2"}`}>{step.desc}</p>
        {step.meta && <p className="label text-ws-accent mt-4">{step.meta}</p>}
      </div>
    </div>
  </Reveal>
);

// ---- Flow diagram (chips with connectors) ----
export const FlowDiagram = ({ items, dark = false }) => (
  <ol data-testid={`flow-${items[0].toLowerCase().replace(/\s+/g, "-")}-${items.length}`} className="flex flex-col sm:flex-row sm:flex-wrap items-start gap-3">
    {items.map((it, i) => (
      <li key={it} className="w-full sm:w-auto max-w-full">
        <Reveal delay={i * 0.05} className="flex flex-col sm:flex-row items-center gap-3">
          <span className={`label inline-flex w-full sm:w-auto justify-center max-w-full px-5 py-3 rounded-full border ${dark ? "border-ws-bg/20 text-ws-bg bg-ws-dark2" : "border-ws-border text-ws-dark bg-ws-card"}`}>{it}</span>
          {i < items.length - 1 && <ArrowRight aria-hidden="true" className="w-4 h-4 shrink-0 rotate-90 sm:rotate-0 text-ws-accent" />}
        </Reveal>
      </li>
    ))}
  </ol>
);

// ---- Certification card ----
export const CertCard = ({ name }) => (
  <Reveal className="group">
    <div data-testid={`cert-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-ws-card border border-ws-border rounded-xl px-4 sm:px-6 py-7 sm:py-9 h-full flex items-center transition-colors hover:border-ws-accent">
      <span className="font-display text-xl sm:text-3xl text-ws-dark whitespace-nowrap">{name}</span>
    </div>
  </Reveal>
);

// ---- Replaceable photo placeholder ----
export const PhotoPlaceholder = ({ src, alt, label, ratio = "aspect-[4/3]" }) => (
  <div className={`relative overflow-hidden rounded-2xl bg-ws-bg2 border border-ws-border ${ratio}`}>
    <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" />
    <span className="absolute top-3 left-3 label text-[0.55rem] bg-ws-dark/80 text-ws-bg backdrop-blur rounded px-2.5 py-1.5 max-w-[85%]">
      {label}
    </span>
  </div>
);
